/**
 * product-card.js
 * Global behavior for Soralady product cards
 * Works anywhere a card with data-sora-card exists
 */

(() => {
  // Prevent double binding if script ever loaded twice
  if (window.__SORA_PRODUCT_CARD_BOUND__) return;
  window.__SORA_PRODUCT_CARD_BOUND__ = true;

  const BTN_SELECTOR = 'button[data-action="add-to-cart"]';
  const CARD_SELECTOR = "[data-sora-card]";

  function getVariantId(card) {
    const raw = card?.dataset?.variantId;
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  async function addToCart(variantId, quantity = 1) {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: variantId, quantity }),
    });

    if (!r.ok) {
      try {
        const j = await r.json();
        throw new Error(j?.description || "Add to cart failed");
      } catch (e) {
        throw new Error(e?.message || "Add to cart failed");
      }
    }

    return r.json();
  }

  document.addEventListener("click", async (e) => {
    const btn = e.target.closest(BTN_SELECTOR);
    if (!btn) return;

    const card = btn.closest(CARD_SELECTOR);
    if (!card) return;

    const variantId = getVariantId(card);
    if (!variantId) return;

    if (btn.dataset.loading === "1") return;
    btn.dataset.loading = "1";

    const oldHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = "Adding…";

    try {
      await addToCart(variantId, 1);
      btn.innerHTML = "Added ✓";
      document.dispatchEvent(new CustomEvent("cart:open"));
      setTimeout(function () { btn.disabled = false; btn.innerHTML = oldHtml; }, 1400);
    } catch (err) {
      console.warn("[ProductCard] add to cart failed:", err);
      btn.disabled = false;
      btn.innerHTML = oldHtml;
    } finally {
      delete btn.dataset.loading;
    }
  });

  // ---- Wishlist hearts (Swym) ---------------------------------------------
  const HEART_SELECTOR = '.sora-wl-cardHeart';

  const swymProduct = (card) => ({
    epi: Number(card.dataset.variantId) || null,
    empi: Number(card.dataset.productId) || null,
    du: card.dataset.url || null,
  });

  const markHeart = (card, added) => {
    const h = card.querySelector(HEART_SELECTOR);
    if (!h) return;
    h.classList.toggle('is-added', !!added);
    h.setAttribute('aria-pressed', added ? 'true' : 'false');
    h.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');
  };

  // Read the current wishlist from Swym and reflect it on the cards
  const syncWishlist = () => {
    const sw = window._swat;
    if (!sw || typeof sw.fetchWrtEventTypeET !== 'function' || !sw.EventTypes) return;
    try {
      sw.fetchWrtEventTypeET((items) => {
        const ids = new Set((items || []).map((it) => String(it.epi || it.di || it.empi || '')));
        document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
          markHeart(card, ids.has(String(card.dataset.variantId || '')));
        });
      }, sw.EventTypes.addToWishList);
    } catch (e) {}
  };

  document.addEventListener('click', (e) => {
    const heart = e.target.closest(HEART_SELECTOR);
    if (!heart) return;
    e.preventDefault();
    e.stopPropagation();

    const card = heart.closest(CARD_SELECTOR);
    const sw = window._swat;
    if (!card || !sw) return;

    const product = swymProduct(card);
    if (!product.epi) return;

    const added = heart.classList.contains('is-added');
    markHeart(card, !added); // optimistic
    const revert = () => markHeart(card, added);
    // The *UsingDu calls are what Swym's own buttons use: they read the product from its URL first
    // and send title, price and photo along. Swym stays silent when it refuses a product, so the
    // hearts are re-read from the list once it answers, or after a few seconds.
    const done = () => syncWishlist();

    try {
      if (added) {
        if (sw.removeFromWishlistUsingDu) sw.removeFromWishlistUsingDu({ du: product.du, epi: product.epi }, done, revert);
        else if (sw.removeFromWishList) sw.removeFromWishList(product, done, revert);
        else revert();
      } else {
        const source = sw.wishlistSources && sw.wishlistSources.plp;
        if (sw.addToWishlistUsingDu) sw.addToWishlistUsingDu({ du: product.du, epi: product.epi, source, qty: 1 }, done, revert);
        else if (sw.addToWishList) sw.addToWishList(product, done);
        else revert();
      }
    } catch (err) {
      revert();
    }
    setTimeout(syncWishlist, 5000);
  });

  // Initial sync once Swym is ready
  const waitSwym = (t0 = Date.now()) => {
    if (window._swat && window._swat.EventTypes) { syncWishlist(); return; }
    if (Date.now() - t0 < 9000) setTimeout(() => waitSwym(t0), 250);
  };
  waitSwym();

  // Re-sync when more cards load (infinite scroll)
  const firstCard = document.querySelector(CARD_SELECTOR);
  const grid = firstCard ? firstCard.parentElement : null;
  if (grid && window.MutationObserver) {
    let t;
    new MutationObserver(() => {
      clearTimeout(t);
      t = setTimeout(syncWishlist, 300);
    }).observe(grid, { childList: true });
  }
})();
